import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useProfileStore } from '@/store/profile.store';
import type { Profile } from '@/store/profile.store';
import '@/styles/analytics.css';

const RADIUS = 45;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

interface FieldCheck {
  label: string;
  filled: boolean;
}

function computeCompleteness(profile: Profile): {
  percentage: number;
  missing: string[];
} {
  const fields: FieldCheck[] = [
    { label: 'First name', filled: !!profile.firstName?.trim() },
    { label: 'Email', filled: !!profile.email?.trim() },
    { label: 'Phone', filled: !!profile.phone?.trim() },
    { label: 'Location', filled: !!profile.location?.trim() },
    { label: 'Summary', filled: !!profile.summary?.trim() },
    { label: 'Experience', filled: profile.experience?.length > 0 },
    { label: 'Education', filled: profile.education?.length > 0 },
    { label: 'Skills', filled: profile.skills?.length > 0 },
  ];

  const filledCount = fields.filter((f) => f.filled).length;
  const percentage = Math.round((filledCount / fields.length) * 100);
  const missing = fields.filter((f) => !f.filled).map((f) => f.label);

  return { percentage, missing };
}

function getCompletenessColor(pct: number): string {
  if (pct >= 75) return 'var(--color-success)';
  if (pct >= 50) return 'var(--color-warning)';
  return 'var(--color-error)';
}

export function ProfileCompleteness() {
  const profile = useProfileStore((s) => s.profile);

  const { percentage, missing } = useMemo(() => {
    if (!profile) return { percentage: 0, missing: [] as string[] };
    return computeCompleteness(profile);
  }, [profile]);

  if (!profile) {
    return (
      <div className="profile-completeness">
        <h2 className="profile-completeness__title">Profile Completeness</h2>
        <div className="profile-completeness__no-profile">
          <p className="profile-completeness__no-profile-text">
            Create a profile to track completeness.
          </p>
          <Link to="/app/onboarding" className="profile-completeness__action">
            Create Profile
          </Link>
        </div>
      </div>
    );
  }

  const color = getCompletenessColor(percentage);
  const offset = CIRCUMFERENCE - (percentage / 100) * CIRCUMFERENCE;

  return (
    <div className="profile-completeness">
      <h2 className="profile-completeness__title">Profile Completeness</h2>

      <div className="profile-completeness__chart">
        <svg width="120" height="120" viewBox="0 0 120 120" aria-label={`Profile ${percentage}% complete`}>
          <circle
            cx="60"
            cy="60"
            r={RADIUS}
            fill="none"
            stroke="var(--color-border)"
            strokeWidth="8"
          />
          <circle
            cx="60"
            cy="60"
            r={RADIUS}
            fill="none"
            stroke={color}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={offset}
            style={{
              transform: 'rotate(-90deg)',
              transformOrigin: 'center',
              transition: 'stroke-dashoffset 1.5s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          />
        </svg>
        <div className="profile-completeness__center-text">
          <span className="profile-completeness__percentage">{percentage}%</span>
        </div>
      </div>

      {percentage === 100 ? (
        <div className="profile-completeness__complete">
          <svg className="profile-completeness__complete-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          <p className="profile-completeness__complete-text">
            Profile complete!
          </p>
        </div>
      ) : (
        <div className="profile-completeness__missing">
          <p className="profile-completeness__missing-title">Missing fields</p>
          <ul className="profile-completeness__missing-list">
            {missing.map((field) => (
              <li key={field} className="profile-completeness__missing-item">
                {field}
              </li>
            ))}
          </ul>
          <Link to="/app/profile" className="profile-completeness__action">
            Complete Profile
          </Link>
        </div>
      )}
    </div>
  );
}
